/**
 * ChatBubble — single message bubble with text and/or images.
 * Image+text = caption layout. Image only = overlay time.
 * Supports long-press context menu (copy, delete).
 */

import React, { useRef, useCallback } from 'react'
import { format } from 'date-fns'

// ── Link detection ─────────────────────────────────────────

const URL_REGEX = /(?:https?:\/\/|www\.)[\w-]+(?:\.[\w-]+)+(?:\/[^\s]*)?/gi

function renderTextWithLinks(text: string): React.ReactNode {
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null = null

  URL_REGEX.lastIndex = 0
  while ((match = URL_REGEX.exec(text)) !== null) {
    // Text before link
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    const url = match[0]
    const href = url.startsWith('http') ? url : `https://${url}`
    parts.push(
      <a
        key={match.index}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-accent underline underline-offset-2 decoration-accent/40
          hover:decoration-accent/70 transition-colors"
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>,
    )
    lastIndex = match.index + url.length
  }

  // Remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : text
}

// ── Types ──────────────────────────────────────────────────

interface ChatBubbleProps {
  id: string
  text: string | null
  mediaUrls: string[]
  time: string
  editedAt: string | null
  isMine: boolean
  isFirstInGroup: boolean
  onImageTap: (urls: string[], index: number) => void
  onLongPress: (messageId: string, text: string | null, isMine: boolean) => void
}

// ── Component ──────────────────────────────────────────────

export const ChatBubble: React.FC<ChatBubbleProps> = React.memo(({
  id,
  text,
  mediaUrls,
  time,
  editedAt,
  isMine,
  isFirstInGroup,
  onImageTap,
  onLongPress,
}) => {
  const hasMedia = mediaUrls.length > 0
  const hasText = text !== null && text.trim().length > 0
  const isImageOnly = hasMedia && !hasText
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const bubbleBase = isMine
    ? 'bg-primary/[0.15] text-text'
    : 'bg-white/[0.05] text-text'

  const radiusMine = isFirstInGroup
    ? 'rounded-[18px] rounded-br-[6px]'
    : 'rounded-[18px] rounded-r-[6px]'

  const radiusTheirs = isFirstInGroup
    ? 'rounded-[18px] rounded-bl-[6px]'
    : 'rounded-[18px] rounded-l-[6px]'

  const radius = isMine ? radiusMine : radiusTheirs

  // ── Long press ────────────────────────────────────────

  const handleTouchStart = useCallback((): void => {
    timerRef.current = setTimeout(() => {
      onLongPress(id, text, isMine)
    }, 500)
  }, [id, text, isMine, onLongPress])

  const handleTouchEnd = useCallback((): void => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const isEdited = editedAt !== null
  const timeStr = `${isEdited ? '✎ ' : ''}${format(new Date(time), 'HH:mm')}`

  return (
    <div
      className={`flex ${isMine ? 'justify-end' : 'justify-start'} ${isFirstInGroup ? 'mt-2' : 'mt-px'}`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchEnd}
      onContextMenu={(e) => { e.preventDefault(); onLongPress(id, text, isMine) }}
    >
      <div className={`max-w-[78%] overflow-hidden ${radius} ${bubbleBase}`}>
        {/* Images */}
        {hasMedia && (
          <div className="relative">
            <ImageGrid
              urls={mediaUrls}
              onTap={(index) => onImageTap(mediaUrls, index)}
            />
            {/* Overlay time for image-only messages */}
            {isImageOnly && (
              <span className="absolute bottom-1.5 right-2 px-1.5 py-0.5 rounded-md
                bg-black/50 backdrop-blur-sm text-[10px] text-white/80 leading-none">
                {timeStr}
              </span>
            )}
          </div>
        )}

        {/* Caption text + time (when text exists) */}
        {hasText && (
          <div className={hasMedia ? 'px-3 pt-1.5 pb-[7px]' : 'px-3 py-[7px]'}>
            <p className="text-[14px] leading-[1.45] whitespace-pre-wrap break-words">
              {renderTextWithLinks(text as string)}
            </p>
            <p className={`text-[10px] mt-0.5 leading-none ${
              isMine ? 'text-primary/40 text-right' : 'text-text-hint/35'
            }`}>
              {timeStr}
            </p>
          </div>
        )}

        {/* Time only for text-only messages (no media, no text renders above) */}
        {!hasMedia && !hasText && (
          <div className="px-3 py-[7px]">
            <p className={`text-[10px] leading-none ${
              isMine ? 'text-primary/40 text-right' : 'text-text-hint/35'
            }`}>
              {timeStr}
            </p>
          </div>
        )}
      </div>
    </div>
  )
})
ChatBubble.displayName = 'ChatBubble'

// ── Image Grid ─────────────────────────────────────────────

interface ImageGridProps {
  urls: string[]
  onTap: (index: number) => void
}

const ImageGrid: React.FC<ImageGridProps> = ({ urls, onTap }) => {
  if (urls.length === 1) {
    return (
      <button
        type="button"
        onClick={() => onTap(0)}
        className="block w-full tap-highlight-transparent"
      >
        <img src={urls[0]} alt="" className="w-full max-h-[280px] object-cover" loading="lazy" />
      </button>
    )
  }

  if (urls.length === 2) {
    return (
      <div className="grid grid-cols-2 gap-px">
        {urls.map((url, i) => (
          <button key={url} type="button" onClick={() => onTap(i)} className="block tap-highlight-transparent">
            <img src={url} alt="" className="w-full h-[140px] object-cover" loading="lazy" />
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-px">
      <button type="button" onClick={() => onTap(0)} className="col-span-2 block tap-highlight-transparent">
        <img src={urls[0]} alt="" className="w-full h-[180px] object-cover" loading="lazy" />
      </button>
      {urls.slice(1).map((url, i) => (
        <button key={url} type="button" onClick={() => onTap(i + 1)} className="block tap-highlight-transparent">
          <img src={url} alt="" className="w-full h-[100px] object-cover" loading="lazy" />
        </button>
      ))}
    </div>
  )
}
